import {useEffect, useMemo, useState} from 'react';
import {useAuth} from '@/context/use-auth';
import {useMembersTranslation} from '@/i18n/hooks';
import {
    getSpecialAssignmentBookings,
    deleteSpecialAssignmentBooking,
    SpecialAssignmentBookingHistoryItem,
} from '@/services/special-assignment-services';
import {formatDateString, formatDateTimeString} from '@/lib/utils';
import SpecialAssignmentBookingDetailDialog from '@/components/special-assignment-booking-detail-dialog';
import SpecialAssignmentEditDialog from '@/components/special-assignment-edit-dialog';
import ConfirmationDialog from '@/components/confirmation-dialog/confirmation-dialog';
import {DataTable, sortableColumnHeader} from '@/components/data-table';
import {ColumnDef} from '@tanstack/react-table';
import {Button} from '@/components/tailwind/button';
import {Badge} from '@/components/tailwind/badge';
import {Eye, Pencil} from 'lucide-react';
import {Text} from '@/components/tailwind/text';
import {useToast} from '@/hooks/use-toast';
import type {SpecialAssignmentService} from '@/services/special-assignment-services';
import log from '@/lib/logger'

interface SpecialAssignmentHistoryTabProps {
    refreshTrigger: number;
    services: SpecialAssignmentService[];
    onBookingUpdated: () => void;
}

export default function SpecialAssignmentHistoryTab({
    refreshTrigger,
    services,
    onBookingUpdated,
}: SpecialAssignmentHistoryTabProps) {
    const {t} = useMembersTranslation();
    const {token} = useAuth();
    const {toast} = useToast();

    const [items, setItems] = useState<SpecialAssignmentBookingHistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
    const [editBookingId, setEditBookingId] = useState<string | null>(null);
    const [deleteBookingId, setDeleteBookingId] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;

        async function loadHistory() {
            setIsLoading(true);
            try {
                const data = await getSpecialAssignmentBookings(token, 0, 999999);
                setItems(data.content);
            } catch (error) {
                log.error('Error loading special assignment bookings:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadHistory();
    }, [token, refreshTrigger]);

    async function handleDelete() {
        if (!deleteBookingId || !token) return;
        try {
            await deleteSpecialAssignmentBooking(token, deleteBookingId);
            toast({
                title: t('specialAssignments.toast.deleteSuccess.title'),
                description: t('specialAssignments.toast.deleteSuccess.message'),
            });
            onBookingUpdated();
        } catch (error) {
            log.error('Error deleting booking:', error);
            toast({
                title: t('specialAssignments.toast.deleteError.title'),
                description: t('specialAssignments.toast.deleteError.message'),
                variant: 'destructive',
            });
        }
    }

    function handleEditSuccess() {
        setEditBookingId(null);
        onBookingUpdated();
        toast({
            title: t('specialAssignments.toast.editSuccess.title'),
            description: t('specialAssignments.toast.editSuccess.message'),
        });
    }

    const columns = useMemo<ColumnDef<SpecialAssignmentBookingHistoryItem>[]>(() => [
        {
            accessorKey: 'activityDate',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.date', 'members'),
            cell: ({row}) => (
                <Text className={row.original.deletedAt ? 'line-through text-gray-400 dark:text-gray-500' : ''}>
                    {formatDateString(row.original.activityDate)}
                </Text>
            ),
        },
        {
            id: 'time',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.time', 'members'),
            cell: ({row}) => (
                <Text className={row.original.deletedAt ? 'line-through text-gray-400 dark:text-gray-500' : ''}>
                    {row.original.startTime && row.original.endTime
                        ? `${row.original.startTime} – ${row.original.endTime}`
                        : '–'}
                </Text>
            ),
        },
        {
            accessorKey: 'serviceName',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.service', 'members'),
            cell: ({row}) => (
                <div className="flex items-center gap-2">
                    <Text className={row.original.deletedAt ? 'line-through text-gray-400 dark:text-gray-500' : ''}>
                        {row.original.serviceName}
                    </Text>
                    {row.original.deletedAt && (
                        <Badge color="red">{t('specialAssignments.history.cancelled')}</Badge>
                    )}
                </div>
            ),
        },
        {
            accessorKey: 'memberCount',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.memberCount', 'members'),
            cell: ({row}) => (
                <Text className={row.original.deletedAt ? 'line-through text-gray-400 dark:text-gray-500' : ''}>
                    {row.original.memberCount}
                </Text>
            ),
        },
        {
            accessorKey: 'pointsPerMember',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.pointsPerMember', 'members'),
            cell: ({row}) => (
                <Text className={row.original.deletedAt ? 'line-through text-gray-400 dark:text-gray-500' : ''}>
                    {row.original.pointsPerMember}
                </Text>
            ),
        },
        {
            accessorKey: 'createdByUsername',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.createdBy', 'members'),
            cell: ({row}) => <Text>{row.original.createdByUsername}</Text>,
        },
        {
            accessorKey: 'createdAt',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.history.headers.createdAt', 'members'),
            cell: ({row}) => <Text>{formatDateTimeString(row.original.createdAt)}</Text>,
        },
        {
            id: 'actions',
            cell: ({row}) => (
                <div className="flex items-center gap-1">
                    <Button plain onClick={() => setSelectedBookingId(row.original.bookingId)}>
                        <Eye size={16}/>
                    </Button>
                    {!row.original.deletedAt && (
                        <Button plain onClick={() => setEditBookingId(row.original.bookingId)}>
                            <Pencil size={16}/>
                        </Button>
                    )}
                </div>
            ),
        },
    ], [t]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-sm text-gray-500 dark:text-gray-400">...</div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('specialAssignments.history.emptyState')}
                </p>
            </div>
        );
    }

    return (
        <div>
            <SpecialAssignmentBookingDetailDialog
                bookingId={selectedBookingId}
                onClose={() => setSelectedBookingId(null)}
            />

            {editBookingId && (
                <SpecialAssignmentEditDialog
                    bookingId={editBookingId}
                    services={services}
                    onClose={() => setEditBookingId(null)}
                    onSuccess={handleEditSuccess}
                    onDelete={() => {
                        setDeleteBookingId(editBookingId);
                        setEditBookingId(null);
                    }}
                />
            )}

            <ConfirmationDialog
                isOpen={!!deleteBookingId}
                onConfirm={handleDelete}
                onCancel={() => setDeleteBookingId(null)}
                config={{
                    title: 'specialAssignments.deleteDialog.title',
                    description: 'specialAssignments.deleteDialog.description',
                    cancelButton: {
                        text: 'specialAssignments.deleteDialog.cancel',
                        ariaLabel: 'specialAssignments.deleteDialog.cancel',
                    },
                    confirmButton: {
                        text: 'specialAssignments.deleteDialog.confirm',
                        ariaLabel: 'specialAssignments.deleteDialog.confirm',
                        color: 'red',
                    },
                }}
                namespace="members"
                testIdPrefix="delete-booking"
            />

            <DataTable columns={columns} data={items}/>
        </div>
    );
}
